/**
 * confirmation-page.tsx — the authoring surface for a journey end page
 * (stage 3, 2026-09-01). The registry, the state and the migration live in
 * `confirmation-sections.ts` (the two files are deliberately not the same
 * basename — one import path, one module); this file is the three pieces of UI that read
 * them, kept out of NewJourneys.tsx for the same reason appt-setup.tsx is.
 *
 *   · CSRail       the left-pane list of the sections this surface offers —
 *                  what is on the page, and what could be. Eye to hide, and an
 *                  add row for what the type allows but the page does not
 *                  carry. NOT draggable and no remove: reordering and removal
 *                  are the canvas's, where the author can see what moves.
 *   · CSCanvas     the page itself, each section rendered against a sample
 *                  booking and selectable like a block.
 *   · CSInspector  the section scope in the right pane: what the section is,
 *                  and the page-level fields it reads.
 *
 * WHAT A SECTION RENDERS IS NOT AUTHORED. Every value below comes from the
 * booking, which does not exist until a customer makes one — so the canvas
 * shows a SAMPLE and says so. The host supplies it (`CSBooking`); nothing in
 * this file invents content, and no section writes anything except the three
 * page fields the inspector owns.
 *
 * ANSWERS is the one section whose sample cannot be right by construction on a
 * queue or an appointment: which questions were asked depends on the service
 * the customer picked. Stage 4 (2026-09-01) gives it the sample-service
 * switcher the Contact page already had — the SAME control and the same
 * per-page memory, supplied by the host as `answersPick`, so a journey cannot
 * be previewing one service here and another there. The host resolves the
 * questions; this file renders them behind a badge naming the service. With no
 * service picked the section says which control to use rather than showing
 * plausible fake questions. On an event there is nothing to pick — one event,
 * one group — so the host passes no switcher and the preview is exact.
 */
import { useState, type ReactNode } from "react";
import {
  CONFIRMATION_SECTIONS, csDef, csForType, csMissing, csMove, csToggle, csRemove, csAdd, csApplies,
  type CSState, type CSSection, type CSType, type CSSurface, type ConfirmationSection,
} from "./confirmation-sections";

/** The canvas's per-section control buttons. Copied in shape from
 *  `block-canvas.tsx`'s CTL on purpose: the two canvases are meant to feel like
 *  one gesture, so the buttons are the same size, colour and radius. */
const CTL = {
  width: 20, height: 20, border: "1px solid #DCCCFC", borderRadius: 4, background: "#FFFFFF",
  color: "#5142C7", fontSize: 10, lineHeight: 1, cursor: "pointer", fontFamily: "inherit",
};

const FONT = '"DM Sans", "Helvetica Neue", sans-serif';
const MONO = { fontFamily: "ui-monospace, monospace" };
const SEC = { fontSize: 10, fontWeight: 800, letterSpacing: "0.09em", textTransform: "uppercase" as const, color: "#697180", marginBottom: 8 };
const INPUT = { width: "100%", boxSizing: "border-box" as const, border: "1px solid #D1D4DC", borderRadius: 6, padding: "8px 10px", fontSize: 12.5, fontFamily: FONT, color: "#333333" };
const NOTE = { border: "1px solid #DCCCFC", background: "#F6F2FE", borderRadius: 8, padding: "8px 10px", fontSize: 10.5, lineHeight: 1.5, color: "#5142C7" };
const cap = (s: string) => s.slice(0, 1).toUpperCase() + s.slice(1);

/* ── the sample booking the canvas renders against ────────────────────────
   One shape for all three journey types; a section reads the fields its type
   has and skips the rest. Everything is optional — the host derives what it
   can from the record it is bound to, and a missing value renders as an empty
   state naming the Setup step that sets it, never as a plausible fake. */
export interface CSBooking {
  brandName?: string;
  /** Merged sections (2026-09-02): the checklist lines, and the live queue
   *  figures. Absent renders the section's empty state, never a plausible fake. */
  prepare?: string[];
  queuePos?: string;
  queueWait?: string;
  /** Queue and appointment: the booked service. Event: the event. */
  serviceName?: string;
  eventName?: string;
  dateLine?: string;
  timeLine?: string;
  durationLine?: string;
  advisor?: string;
  ticketLine?: string;
  placeName?: string;
  address?: string;
  /** Phone or video: Place is suppressed at runtime and Meeting link renders. */
  virtual?: boolean;
  /** LOCAL PATCH (platform side, 2026-09-08, JAI-1292): the live join link and passcode, from the SESSION view's virtual
   *  location (null until the server reveals it). The `meeting` arm draws the real
   *  link when present and nothing on the live surface otherwise. */
  joinUrl?: string | null;
  passcode?: string | null;
  reference?: string;
  guests?: string[];
  answers?: { label: string; value: string }[];
  /** "Sample · Mortgage review" — set when the answers came from a service the
   *  DESIGNER picked rather than from the journey's own group. */
  answersBadge?: string;
  promo?: { title: string; sub?: string } | null;
  footerLines?: string[];
  /** The customer's OWN details, for the yourDetails section (2026-09-07). Held
   *  separately from the tenant-data fields above because these are the only
   *  values on the page that masking touches. */
  customerName?: string;
  customerEmail?: string;
}

export interface CSPageFields { bannerTitle?: string; cancelHours?: string; note?: string; surveyPrompt?: string }

/** What HAPPENED to the booking this page is rendering.
 *
 *  The journey end page has only one of these — it is shown seconds after
 *  booking, so the booking is confirmed by construction, and `confirmed` is the
 *  default for that reason. The BOOKING CONFIRMATION PAGE, opened from an
 *  emailed link days or months later, can be any of them, which is why its
 *  designer has a State selector. Wiring it here rather than in that designer is
 *  what keeps one canvas: the outcome is a property of the booking, and the
 *  canvas already renders the booking. */
export type CSPageState = "confirmed" | "cancelled" | "rescheduled" | "past" | "expired" | "closed";

const OUTCOME: Record<CSPageState, { icon: string; fg: string; bg: string; title: string; sub: (hrs: string) => string }> = {
  confirmed:   { icon: "✓", fg: "#1D8348", bg: "#E6F4EE", title: "You're all set", sub: () => "A confirmation is on its way to your email." },
  cancelled:   { icon: "✕", fg: "#D01647", bg: "#FDEEF1", title: "This booking is cancelled", sub: () => "Nothing further is needed from you — you can book again below." },
  rescheduled: { icon: "⇄", fg: "#1B58B8", bg: "#E8F1FD", title: "Your booking has moved", sub: () => "The details below are the new time. The original has been released." },
  past:        { icon: "◷", fg: "#5A6070", bg: "#F0F1F4", title: "This booking has passed", sub: () => "The details below are kept for your records." },
  expired:     { icon: "!", fg: "#B9770E", bg: "#FDF0E3", title: "This link is no longer valid", sub: () => "Request a fresh one from your confirmation email, or contact the branch." },
  closed:      { icon: "◷", fg: "#B9770E", bg: "#FDF0E3", title: "You're all set", sub: (hrs) => `This booking is within ${hrs} hours, so it can no longer be changed online.` },
};

/** On an invalid link there is no booking to show — only who the page is from and
 *  what went wrong. Anything else would be leaking a booking to a URL that has
 *  just been judged not to identify one. */
const EXPIRED_ONLY = ["brandHeader", "banner", "footer"];

/** The sample-service switcher, when the journey type has one to offer. The
 *  host owns the list, the current pick and where it is remembered; passing
 *  `""` to `onPick` clears it. */
export interface CSAnswersPick {
  options: { id: string; name: string }[];
  selected: string;
  onPick: (id: string) => void;
}

/** The rail's control is a CHECKBOX, not an eye (operator, 2026-09-03).
 *
 *  The rail reads as a list of the sections this surface offers, so a row is a
 *  question with two answers: on the page, or not. An eye glyph (◉/◌) answered
 *  it in one direction only — it turned a present section off, but an ABSENT
 *  section's row was a dashed "+ add" affordance, so checking was possible and
 *  unchecking was not. Two different controls for one binary, and the asymmetry
 *  read as a broken checkbox rather than as two states.
 *
 *  One box now, in both directions. Unchecking a section HIDES it rather than
 *  removing it: it keeps its place and its configuration, so re-checking returns
 *  it to where the author put it. Destructive removal stays on the canvas, where
 *  the author can see what is being removed. */
const CheckBox = (props: { on: boolean; dim?: boolean; locked?: boolean }) => (
  <span style={{
    width: 15, height: 15, borderRadius: 4, flex: "none", display: "inline-flex",
    alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900,
    border: `1.5px solid ${props.on ? (props.locked ? "#9AA1AE" : "#5142C7") : "#C9CDD6"}`,
    background: props.on ? (props.locked ? "#9AA1AE" : "#5142C7") : "#FFFFFF",
    color: "#FFFFFF", opacity: props.dim ? 0.55 : 1,
  }}>{props.on ? "✓" : ""}</span>
);

/* ══ the rail ══════════════════════════════════════════════════════════ */
export function CSRail(props: {
  state: CSState;
  type: CSType;
  sel: string | null;
  onSelect: (id: string | null) => void;
  onChange: (next: CSState) => void;
  /** Which page this rail is authoring — see CSSurface. Defaults to the journey
   *  end page, so the journey designer's call is unchanged. */
  surface?: CSSurface;
  /** Rendered under the section list, above the "sections are not blocks" line.
   *  The booking page's block rail lives here rather than in a second rail. */
  children?: ReactNode;
}) {
  const { state, type, sel, onSelect, onChange } = props;
  const surface = props.surface ?? "end";
  const missing = csMissing(state, type, surface);

  /* Selection is by UID, not by section id: two rows can share a type on a
     document this designer did not write, and a uid is what survives a
     reorder. */
  const row = (r: CSSection) => {
    const d = csDef(r.type);
    const off = !!r.hidden;
    const on = sel === r.uid;
    /* A type the registry does not know is KEPT and shown as itself. Dropping
       it would delete an author's section because this build is older than the
       document. */
    if (!d) return (
      <div key={r.uid} onClick={() => onSelect(on ? null : r.uid)}
        style={{ display: "flex", alignItems: "center", gap: 7, padding: "6px 7px", borderRadius: 7, cursor: "pointer",
          border: `1px solid ${on ? "#5142C7" : "#E6E8EC"}`, background: on ? "#F6F2FE" : "#FFFFFF", opacity: off ? 0.55 : 1 }}>
        <span onClick={(e) => { e.stopPropagation(); onChange(csToggle(state, r.uid)); }}
          title={off ? "Put it back on the page" : "Take it off the page — it keeps its place"}>
          <CheckBox on={!off} />
        </span>
        <span style={{ width: 20, height: 20, borderRadius: 6, background: "#FDF0E3", color: "#B9770E", fontSize: 10, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>?</span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, fontWeight: 600, color: "#697180" }}>{r.type}</span>
        <span title="This build does not know this section. It is kept exactly as saved." style={{ fontSize: 9, color: "#B9770E", flex: "none", ...MONO }}>kept</span>
      </div>
    );
    /* Applies-to is greyed, never hidden: an author who cannot find Guests
       concludes the designer is broken, not that it does not apply here. */
    const na = !csApplies(r.type, type, surface);
    /* NOT DRAGGABLE, and no remove (operator, 2026-09-02). The rail is a LIST of
       the sections this surface offers — what is on the page and what could be.
       Reordering and removing happen on the canvas, where the author can see
       what moves; two places to reorder meant two gestures for one outcome, and
       a rail drag moved something the author could not see. The eye stays: hide
       is a property of the row, not a rearrangement of the page. */
    return (
      <div
        key={r.uid}
        onClick={() => onSelect(on ? null : r.uid)}
        title={na ? `${d.label} does not render on a ${type} — it stays on the page, greyed.` : undefined}
        style={{
          display: "flex", alignItems: "center", gap: 7, padding: "6px 7px", borderRadius: 7, cursor: "pointer",
          border: `1px solid ${on ? "#5142C7" : "#E6E8EC"}`,
          background: on ? "#F6F2FE" : "#FFFFFF",
          opacity: off || na ? 0.55 : 1,
        }}>
        {d.locked ? (
          <span title="Locked on — a confirmation page without it reads as a phishing page.">
            <CheckBox on locked />
          </span>
        ) : (
          <span onClick={(e) => { e.stopPropagation(); onChange(csToggle(state, r.uid)); }}
            title={off ? "Put it back on the page" : "Take it off the page — it keeps its place"}>
            <CheckBox on={!off} />
          </span>
        )}
        <span style={{ width: 20, height: 20, borderRadius: 6, background: d.tint, color: d.color, fontSize: 10, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>{d.glyph}</span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, fontWeight: 600, color: on ? "#5142C7" : "#333333", textDecoration: off ? "line-through" : "none" }}>{d.label}</span>
        {na ? <span style={{ fontSize: 9, color: "#9AA1AE", flex: "none", ...MONO }}>n/a</span> : null}
        {d.locked ? <span title="Locked on — it cannot be taken off the page." style={{ fontSize: 9, flex: "none" }}>🔒</span> : null}
      </div>
    );
  };

  /* An OFF-PAGE section, in the SAME list as the ones on the page (operator,
     2026-09-02). It used to be a separate row of chips under the list, which
     read as a different kind of thing and made the author look in two places to
     answer one question — which sections does this page have? Now the list is
     every section the surface offers, and the row itself says whether it is on.
     Dashed and dimmed, so "available" is never mistaken for "present". */
  const addRow = (d: ConfirmationSection) => (
    <div
      key={`add-${d.id}`}
      onClick={() => { const { state: next, uid } = csAdd(state, d.id); onChange(next); onSelect(uid); }}
      title={d.help}
      style={{ display: "flex", alignItems: "center", gap: 7, padding: "6px 7px", borderRadius: 7, cursor: "pointer",
        border: "1px dashed #D9DCE3", background: "#FCFCFD" }}>
      <CheckBox on={false} />
      <span style={{ width: 20, height: 20, borderRadius: 6, background: d.tint, color: d.color, fontSize: 10, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none", opacity: 0.6 }}>{d.glyph}</span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, fontWeight: 600, color: "#9AA1AE" }}>{d.label}</span>
    </div>
  );

  return (
    <>
      <div style={SEC}>Sections</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
        {state.map((r) => row(r))}
        {missing.map(addRow)}
      </div>
      <div style={{ fontSize: 10, lineHeight: 1.5, color: "#B5BAC6", marginBottom: 18, ...MONO }}>
        tick to put a section on the page · untick keeps its place and its settings
      </div>
      {props.children}
    </>
  );
}

/* ══ the canvas ════════════════════════════════════════════════════════ */
const rowLine = (k: string, v: string) => (
  <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 11.5 }}>
    <span style={{ color: "#697180", flex: "none" }}>{k}</span><b style={{ textAlign: "right" }}>{v}</b>
  </div>
);
const empty = (text: string) => (
  <div style={{ border: "1px dashed #D1D4DC", borderRadius: 9, padding: "12px 11px", fontSize: 11, lineHeight: 1.5, color: "#697180" }}>{text}</div>
);
const runtimeNote = (text: string) => (
  <div style={{ fontSize: 9.5, color: "#B5BAC6", marginTop: 6, ...MONO }}>{text}</div>
);
/** A real control. Rendered identically in the designer and on the customer's
 *  page — inert there, because `onClick` is absent, not because it is drawn
 *  differently. The 44px minimum is the touch target: this is a phone surface
 *  far more often than a desktop one. */
const CtlBtn = (props: {
  label: string; accent: string; primary?: boolean; onClick?: () => void;
  busy?: boolean; locked?: boolean;
}) => {
  const inert = !props.onClick || props.locked;
  return (
    <button type="button" disabled={inert}
      onClick={props.onClick ? () => props.onClick!() : undefined}
      style={{
        minHeight: 44, padding: "0 18px", borderRadius: 10, fontSize: 13, fontWeight: 700,
        fontFamily: "inherit", cursor: inert ? "default" : "pointer",
        border: props.primary ? "none" : `1.5px solid ${props.accent}`,
        background: props.primary ? props.accent : "#FFFFFF",
        color: props.primary ? "#FFFFFF" : props.accent,
        opacity: props.locked ? 0.5 : 1,
      }}>{props.busy ? "Working…" : props.label}</button>
  );
};
const ctlRow = (kids: React.ReactNode) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{kids}</div>
);

/** Was #1F8A5B, which measures ~4.35:1 on white — fine for the 11px pills this
 *  section used to draw, under the 4.5:1 floor now that the same colour carries
 *  13px bold button labels. */
const DL_GREEN = "#1A7550";

const pillRow = (labels: string[], accent: string) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
    {labels.map((l) => <span key={l} style={{ border: `1px solid ${accent}`, color: accent, borderRadius: 999, padding: "5px 12px", fontSize: 11, fontWeight: 700 }}>{l}</span>)}
  </div>
);

function sectionBody(d: ConfirmationSection, type: CSType, b: CSBooking, f: CSPageFields, accent: string, pick: CSAnswersPick | null, st: CSPageState, ctx: CSRenderCtx) {
  /* THE SAME BODIES SERVE THE DESIGNER AND THE CUSTOMER, with two things
     suppressed on the live surface:

     · the grey monospace runtimeNotes. They are notes to the AUTHOR about what
       the section will do at runtime ("the booking reference", "this sample
       booking is virtual"). On a customer's page they are stage directions read
       out loud.
     · the dashed empty states. "No location on the booking yet — a place
       appears here once the journey is attached to one" is authoring guidance;
       to a customer it is a defect notice about their own booking. A section
       with nothing to say renders nothing. */
  const note = (t: string) => (ctx.live ? null : runtimeNote(t));
  const blank = (t: string) => (ctx.live ? null : empty(t));
  switch (d.id) {
    case "brandHeader":
      if (ctx.live && !b.brandName) return null;
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{ width: 26, height: 26, borderRadius: 7, background: accent, color: "#FFFFFF", fontSize: 12, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{(b.brandName ?? "A").slice(0, 1)}</span>
          <span style={{ fontSize: 13, fontWeight: 800 }}>{b.brandName ?? "Your tenant"}</span>
          <span style={{ flex: 1 }} />
          {note("journey branding")}
        </div>
      );
    case "banner": {
      const o = OUTCOME[st];
      /* The headline override applies to a plain confirmation only. On any other
         outcome the wording IS the outcome — an author's "You're all set" over a
         cancellation would be a lie the page tells for them. */
      const headline = st === "confirmed" || st === "closed" ? (f.bannerTitle || o.title) : o.title;
      return (
        <div style={{ textAlign: "center", padding: "4px 0" }}>
          <span style={{ width: 38, height: 38, borderRadius: 999, background: o.bg, color: o.fg, fontSize: 17, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{o.icon}</span>
          <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.01em", marginTop: 8, color: o.fg }}>{headline}</div>
          <div style={{ fontSize: 11.5, color: "#697180", marginTop: 4, lineHeight: 1.55 }}>{o.sub(f.cancelHours || "24")}</div>
          {st === "confirmed" ? note("wording and colour follow the outcome — confirmed · pending · waitlisted") : null}
        </div>
      );
    }
    case "summary": {
      const rows: [string, string][] = type === "event"
        ? [["Event", b.eventName ?? "—"], ["Date", b.dateLine ?? "—"], ["Time", b.timeLine ?? "—"], ["Venue", b.placeName ?? "—"], ...(b.ticketLine ? [["Tickets", b.ticketLine] as [string, string]] : [])]
        : type === "appointment"
        ? [["Service", b.serviceName ?? "—"], ["Date", b.dateLine ?? "—"], ["Time", b.timeLine ?? "—"], ["Duration", b.durationLine ?? "—"], ["With", b.advisor ?? "—"]]
        : [["Service", b.serviceName ?? "—"], ["Ticket", b.ticketLine ?? b.reference ?? "—"], ["Where", b.placeName ?? "—"]];
      return <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{rows.map(([k, v]) => rowLine(k, v))}</div>;
    }
    case "place":
      return b.placeName ? (<>
        <div style={{ fontSize: 12.5, fontWeight: 700 }}>{b.placeName}</div>
        <div style={{ fontSize: 11.5, color: "#697180", marginTop: 2 }}>{b.address || "no address on the location"}</div>
        {b.virtual ? note("this sample booking is virtual — Place is suppressed at runtime") : null}
      </>) : blank("No location on the booking yet — a place appears here once the journey is attached to one.");
    case "meeting": {
      /* A join link is a LIVE capability, so it follows the booking's outcome
         and not just its channel. Offering "Join the meeting" on a cancelled or
         finished booking sends the customer to a room nobody is in — found on
         the seeded 1003/1004 previews, where it rendered under a banner that had
         just said the booking was over.
         The designer still shows the row for every state: it is showing what the
         section can carry, and an author needs to see it to place it. */
      /* LOCAL PATCH (platform side, 2026-09-08, JAI-1292): on the live surface this row is
         about the booking's REAL meeting, or nothing. `b.virtual` is set only from the
         session's virtual location, so an in-person or phone booking renders nothing here
         (CD's arm drew a 'Join the meeting' label with no link on every appointment, and
         the "no longer active" copy on every cancelled one). For a virtual booking: the
         cancelled/past copy, else the real `join_url` (null until the server reveals it)
         plus the passcode, else nothing. The designer preview keeps CD's arm as it was. */
      if (ctx.live) {
        if (!b.virtual) return null;
        if (st === "cancelled" || st === "past") {
          return <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.6 }}>The meeting link is no longer active.</div>;
        }
        if (!b.joinUrl) return null;
        return (<>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <span style={{ width: 26, height: 26, borderRadius: 7, background: "#EDE5FE", color: "#5142C7", fontSize: 12, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>▷</span>
            <a href={b.joinUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12.5, fontWeight: 700, color: accent }}>Join the meeting</a>
          </div>
          {b.passcode ? <div style={{ fontSize: 11.5, color: "#697180", marginTop: 4 }}>Passcode: {b.passcode}</div> : null}
        </>);
      }
      return (<>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{ width: 26, height: 26, borderRadius: 7, background: "#EDE5FE", color: "#5142C7", fontSize: 12, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>▷</span>
          <span style={{ fontSize: 12.5, fontWeight: 700 }}>Join the meeting</span>
        </div>
        {note(st === "cancelled" || st === "past" ? "withdrawn at runtime once the booking is over or cancelled" : "renders only for a phone or video booking")}
      </>);
    }
    case "guests":
      return b.guests && b.guests.length
        ? <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{b.guests.map((g) => <span key={g} style={{ border: "1px solid #E7A4B8", background: "#FDF3F6", color: "#D61A55", borderRadius: 999, padding: "4px 10px", fontSize: 11, fontWeight: 700 }}>{g}</span>)}</div>
        : blank("Guests captured at registration list here — this event allows none yet (Setup · Registration).");
    case "answers": {
      /* The switcher is the Contact page's control, in the host's hands: same
         rows, same per-page memory. It is rendered even with a pick already
         made, so changing service is one click rather than a deselect. */
      const chips = pick && pick.options.length ? (
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
          <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "#8A92A0" }}>Preview as</span>
          {pick.options.map((o) => {
            const on = o.id === pick.selected;
            return (
              <button key={o.id} onClick={(e) => { e.stopPropagation(); pick.onPick(on ? "" : o.id); }}
                style={{ padding: "3px 9px", borderRadius: 999, cursor: "pointer", fontFamily: FONT, fontSize: 10.5, fontWeight: 700, border: `1px solid ${on ? "#5142C7" : "#D9DCE3"}`, background: on ? "#5142C7" : "#FFFFFF", color: on ? "#FFFFFF" : "#4B5563", whiteSpace: "nowrap" }}>
                {o.name}
              </button>
            );
          })}
        </div>
      ) : null;
      const body = b.answers && b.answers.length ? (<>
        {b.answersBadge ? <div style={{ display: "inline-block", background: "#FFF7E8", color: "#8A5A08", border: "1px solid #F3D9A4", borderRadius: 999, padding: "2px 9px", fontSize: 9.5, fontWeight: 800, marginBottom: 7 }}>{b.answersBadge}</div> : null}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{b.answers.map((a) => rowLine(a.label, a.value))}</div>
        {type === "event" ? null : note("the customer sees the questions their own service asked")}
      </>) : blank(
        type === "event"
          ? "The event's booking-question answers appear here — attach a group in Setup · Registration."
          : !pick || !pick.options.length
          ? "The questions came from the SERVICE the customer picked, so this page cannot show fixed content — each service asks its own. This journey offers none yet; they are set per service in Setup."
          : pick.selected
          ? "This service asks no questions — a booking through it reaches this page with nothing to list."
          : "Each service asks its own questions, so pick one above to preview what a customer who booked it would see here.");
      /* Edit answers lives HERE (2026-09-07), with the answers it edits, rather
         than in a card of its own. Policy-shaped like everything else: absent
         when the server does not allow it, and nothing at all until the session
         has said. It also only appears where there are answers to edit — an
         Edit button over an empty state edits nothing. */
      /* One rule for both surfaces: the control appears where there are answers
         to edit, and on the live surface only once policy has arrived and allows
         it. An Edit button over an empty state edits nothing — and a designer
         that showed one anyway would be previewing a page the customer never
         gets, which is the divergence the manage and downloads sections above
         were just unified to prevent. */
      const canEdit = ctx.live
        ? !!(ctx.wiring?.policyKnown && ctx.wiring.canEditAnswers)
        : true;
      const editBtn = canEdit && b.answers && b.answers.length
        ? <div style={{ marginTop: 10 }}>{ctlRow([
            <CtlBtn key="ed" label="Edit my answers" accent="#5142C7"
              onClick={ctx.live ? ctx.wiring?.onEditAnswers : undefined}
              busy={ctx.live && ctx.wiring?.busy === "answers"}
              locked={!!(ctx.live && ctx.wiring?.busy && ctx.wiring.busy !== "answers")} />,
          ])}</div>
        : null;
      /* LOCAL PATCH (platform side, 2026-09-09): an empty fragment is not
         "nothing" to the canvas, which decides on `null`. */
      if (chips === null && body === null && editBtn === null) return null;
      return <>{chips}{body}{editBtn}</>;
    }
    case "reference":
      return (<>
        <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: "0.06em", ...MONO }}>{b.reference ?? (type === "queue" ? "A‑042" : "BK‑8F3C21")}</div>
        {note(type === "queue" ? "the queue ticket number" : "the booking reference")}
      </>);
    case "downloads": {
      /* REAL BUTTONS since 2026-09-07 (operator: the customer sees exactly the
         authored page). This section IS the download affordance now — the
         platform's action card that used to carry them is gone from the
         customer surface, so a section left off the page means the documents
         are not offered. That is the point of the ruling.

         Every one is a SERVER-OWNED FLAG, rendered and never probed: a button
         that discovers it is unavailable by earning a 403 has turned a refusal
         into an affordance. The designer has no flags and shows all of them,
         because it is showing what the section can carry. */
      const w = ctx.wiring;
      const green = DL_GREEN;

      /* ONE PATH, for the same reason as the manage section. The designer branch
         used to return all four buttons unconditionally, so previewing
         Cancelled showed "Add to calendar" while the live page withdrew it — the
         author was previewing a state the customer never gets. State gating now
         applies to both surfaces; only the handlers and the server flags differ.
         Nothing until the session says what is permitted: every control here
         needs a handler the session owns, so before it lands the honest
         rendering is no section rather than dead buttons. */
      if (ctx.live && !w?.policyKnown) return null;
      const may = (flag: boolean | undefined) => (ctx.live ? flag === true : true);
      const busyIs = (k: string) => ctx.live && w?.busy === k;
      const lockedFor = (k: string) => !!(ctx.live && w?.busy && w.busy !== k);

      /* A cancelled or finished booking is not addable to a calendar and its
         pass is spent. The PDF and the export stay: the record still happened,
         and that is what those documents are. */
      const spent = st === "past" || st === "cancelled";
      const kids = [
        !spent ? <CtlBtn key="ics" label="Add to calendar" accent={green} onClick={ctx.live ? w?.onIcs : undefined} busy={busyIs("ics")} locked={lockedFor("ics")} /> : null,
        /* The wallet pass has no operation behind it yet, so on the live surface
           it is not drawn at all rather than drawn dead — the server flag is
           false and stays false until one exists. The designer shows it because
           it is showing what the section can carry; when the flag flips it joins
           `kids` here and nothing else changes. */
        !spent && (ctx.live ? ctx.actions?.walletPass === true : true)
          ? <CtlBtn key="pass" label="Wallet pass" accent={green} onClick={undefined} /> : null,
        /* TWO flags, one question each, and the button needs both:
             `canPdf`     (session) — may THIS BOOKING use it
             `summaryPdf` (public)  — does the OPERATION exist on this tenant
           Visibility and handler both come from `canPdf`, so they cannot
           disagree; `summaryPdf` is the separate precondition. There is no
           `ctx.live ||` bypass in front of it — that short-circuited the
           precondition on the live surface, which left a tenant with the
           operation switched off drawing a working button that could only earn a
           refusal, and made the read dead code on both surfaces. The designer
           passes no `actions`, so `undefined !== false` keeps the control
           visible there, which is what showing-what-it-can-carry means. */
        may(w?.canPdf) && ctx.actions?.summaryPdf !== false
          ? <CtlBtn key="pdf" label="Download PDF" accent={green} onClick={ctx.live ? w?.onPdf : undefined} busy={busyIs("pdf")} locked={lockedFor("pdf")} /> : null,
        may(w?.dataExport) ? <CtlBtn key="exp" label="Export my data" accent={green} onClick={ctx.live ? w?.onExport : undefined} busy={busyIs("export")} locked={lockedFor("export")} /> : null,
      ].filter(Boolean);
      if (!kids.length) return <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.6 }}>No documents are available for this booking.</div>;
      return (<>
        {ctlRow(kids)}
        {note("each follows a server flag — wallet pass is not built yet, so it renders only where the flag says so")}
      </>);
    }
    case "manage": {
      /* REAL Move and Cancel since 2026-09-07. They open the manage surface's
         own dialogs and inherit its step-up: any call that comes back
         `403 verificationRequired` parks on the verification prompt and retries
         after the code, which is why nothing here asks for a code itself.

         POLICY IS ABSENT-MEANS-NOT-OFFERED. A button the server would refuse is
         not drawn disabled with an explanation — it is not drawn. The one
         exception is the closed window, because "you could have changed this
         until Tuesday" is something the customer needs told. */
      const amber = "#916D0F";
      const w = ctx.wiring;

      /* ONE PATH, not a live branch and a designer branch (2026-09-07).
         Splitting them is how they disagreed: the live path added See what
         changed to the gated states and the designer path returned the reason
         alone, so an author previewing Cancelled saw no history affordance
         while the real customer got a button. The whole point of the shared
         canvas is that these two cannot diverge, so the only difference below
         is whether a handler exists — which is already how every control here
         stays inert in the designer. */
      if (ctx.live && !w?.policyKnown) {
        /* Policy not arrived. "Please contact the branch" is the answer for a
           tenant that does not offer self-service, NOT for an answer still in
           flight, so nothing renders until it lands. */
        return null;
      }
      /* In the designer every affordance the section CAN carry is shown — it is
         showing what the section is, not what one booking permits. */
      const may = (flag: boolean | undefined) => (ctx.live ? flag === true : true);
      const busyIs = (k: string) => ctx.live && w?.busy === k;
      const lockedFor = (k: string) => !!(ctx.live && w?.busy && w.busy !== k);

      /* See what changed survives every gate Move and Cancel do not. A
         cancelled, past or window-closed booking is exactly when a customer
         wants to read what happened to it — it is a READ, so no window bounds
         it. */
      const histBtn = may(w?.canHistory)
        ? <CtlBtn key="hist" label="See what changed" accent={amber} onClick={ctx.live ? w?.onHistory : undefined} busy={busyIs("history")} locked={lockedFor("history")} />
        : null;

      const reason = st === "cancelled" ? "This booking is already cancelled."
        : st === "past" ? "Past bookings cannot be changed."
        : st === "closed" || (ctx.live && w?.windowClosed)
          ? `Changes closed ${f.cancelHours || "24"} hours before the start time. Call the branch to move or cancel.`
          : "";
      if (reason) {
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.6 }}>{reason}</div>
            {histBtn ? ctlRow([histBtn]) : null}
          </div>
        );
      }

      const kids = [
        may(w?.canMove) ? <CtlBtn key="mv" primary label="Move this booking" accent={amber} onClick={ctx.live ? w?.onMove : undefined} busy={busyIs("move")} locked={lockedFor("move")} /> : null,
        may(w?.canCancel) ? <CtlBtn key="cx" label="Cancel booking" accent={amber} onClick={ctx.live ? w?.onCancel : undefined} busy={busyIs("cancel")} locked={lockedFor("cancel")} /> : null,
        histBtn,
      ].filter(Boolean);
      /* Nothing permitted and the window still open means the tenant does not
         offer self-service on this booking. Say that, rather than printing a
         window that turns out to buy nothing. */
      if (!kids.length) return <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.6 }}>To move or cancel this booking, please contact the branch.</div>;
      return (<>
        {ctlRow(kids)}
        {note(`free until ${f.cancelHours || "24"} hours before — after that Move and Cancel are withdrawn, the reason replaces them, and See what changed stays`)}
      </>);
    }
    case "yourDetails": {
      /* The customer's own name and email (2026-09-07). This section exists
         because the platform's "Your details" card was removed from the
         customer surface — so if an admin does not place it, these details are
         not shown, which is the ruling working as intended.

         MASKING IS READ, NEVER INFERRED. `w.masked` comes from the response's
         manifest; the value on the wire is ALREADY masked when it is on, so
         nothing here does the masking — doing it twice is how the two
         disagree. Reveal runs the manage surface's own step-up. */
      const w = ctx.wiring;
      const blue = "#2D60EB";
      const lines: { k: string; v: string }[] = [];
      if (b.customerName) lines.push({ k: "Name", v: b.customerName });
      if (b.customerEmail) lines.push({ k: "Email", v: b.customerEmail });
      if (!lines.length) {
        return ctx.live
          ? <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.6 }}>We do not hold personal details against this booking.</div>
          : blank("The customer's name and email render here, from the booking.");
      }
      return (<>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {lines.map((l) => rowLine(l.k, l.v))}
        </div>
        {w?.masked ? (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 11, color: "#697180", lineHeight: 1.55, marginBottom: 8 }}>
              Part of your details is hidden while this link is unverified.
            </div>
            {w.canReveal
              ? ctlRow([<CtlBtn key="rv" label="Show my details" accent={blue} onClick={w.onReveal} busy={w.busy === "reveal"} locked={!!w.busy && w.busy !== "reveal"} />])
              : null}
          </div>
        ) : null}
        {ctx.live ? null : note("masked on a tenant with masking on, with a reveal step — in the clear on the common tenant, where masking defaults off")}
      </>);
    }
    case "another": {
      /* LOCAL PATCH (platform side, 2026-09-08, JAI-1292): on the live surface the control rides `wiring.onBookAnother` — the
         session's `book_another` action, the same door the deleted ActionsCard used —
         and is NOT drawn when that door is absent, per the rule the downloads section
         states for the wallet pass: never drawn dead. The designer keeps the label. */
      if (ctx.live) {
        const go = ctx.wiring?.onBookAnother;
        return go ? ctlRow([<CtlBtn key="ba" label="Book another" accent={accent} onClick={go} />]) : null;
      }
      return <span style={{ fontSize: 12, fontWeight: 700, color: accent, textDecoration: "underline" }}>Book another</span>;
    }
    case "promo":
      return b.promo ? (
        <div style={{ border: "1px solid #DCCCFC", background: "#FAF8FF", borderRadius: 10, padding: "10px 12px" }}>
          <div style={{ fontSize: 12.5, fontWeight: 700 }}>{b.promo.title}</div>
          {b.promo.sub ? <div style={{ fontSize: 11, color: "#697180", marginTop: 2 }}>{b.promo.sub}</div> : null}
        </div>
      ) : blank("One promoted service or event shows here — pick it in the inspector.");
    case "footer":
      if (ctx.live && !b.footerLines?.length && !f.note) return null;
      return (<>
        <div style={{ fontSize: 10.5, lineHeight: 1.6, color: "#697180" }}>
          {(b.footerLines ?? (ctx.live ? [] : ["Tenant address", "Contact · Privacy · Terms"])).map((l) => <div key={l}>{l}</div>)}
        </div>
        {f.note ? <div style={{ fontSize: 11, color: "#333333", marginTop: 6, lineHeight: 1.5 }}>{f.note}</div> : null}
      </>);
    /* Merged from the booking confirmation page's registry, 2026-09-02. */
    case "prepare":
      return (b.prepare && b.prepare.length) ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {b.prepare.map((l) => (
            <div key={l} style={{ display: "flex", gap: 7, fontSize: 11.5, color: "#4B5563" }}>
              <span style={{ color: accent, flex: "none" }}>☑</span><span>{l}</span>
            </div>
          ))}
        </div>
      ) : blank("What to bring — ID, documents, what to wear. Add the lines in the inspector.");
    case "queuePos":
      return (<>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em", ...MONO }}>{b.queuePos ?? "3"}</span>
          <span style={{ fontSize: 11.5, color: "#697180" }}>in line · about {b.queueWait ?? "12 min"} to wait</span>
        </div>
        {note("live — this counts down while the page is open")}
      </>);
    case "survey":
      /* `afterTheFact` already keeps this off the journey end page. The state is
         the second half of the same rule: on the linked page it still only means
         anything once the booking has actually happened. */
      return st === "past" ? (<>
        <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>{f.surveyPrompt || "How did it go?"}</div>
        {pillRow(["★", "★", "★", "★", "★"], "#B9770E")}
      </>) : blank("Invites a response once the booking has passed — switch the state to Past to preview it.");
    default:
      return null;
  }
}

/**
 * What the page can actually DO, and who does it (2026-09-07, operator ruling:
 * the customer sees exactly the authored page, nothing extra).
 *
 * The platform's own action card and details card are gone from the customer
 * surface, so Move, Cancel, the documents and the customer's details are now
 * the authored sections' own controls. That makes the canvas render real
 * buttons — which is also why this is ONE object passed in rather than logic
 * inside the canvas: the canvas knows how a section LOOKS, and the surface that
 * owns the session knows what is permitted and what pressing it does.
 *
 * ABSENT HANDLERS ARE THE DESIGNER. With no wiring the sections render the same
 * controls, inert, so the author previews exactly what ships instead of a line
 * of prose standing in for a button. That is the whole point of one canvas.
 *
 * POLICY IS THE SERVER'S. `canMove`/`canCancel` come from the manage view's
 * `actions` and are ABSENT-means-not-offered, never a disabled button with an
 * explanation — a cancellation window reimplemented here would drift from the
 * server's. `windowClosed` is the one exception, because "you could have done
 * this until Tuesday" is information the customer needs.
 */
export interface CSWiring {
  /** Has the SERVER's policy arrived yet?
   *
   *  This is the difference between "not offered" and "not known yet", and
   *  collapsing the two put false copy on the customer's page: the authored page
   *  renders BEFORE the session exchange (that is the whole point of the public
   *  lane), so during the exchange `canMove`/`canCancel` are merely absent — and
   *  absent was being read as the server withholding them. The customer was told
   *  to phone the branch, for a second or two, and then handed working Move and
   *  Cancel buttons. Downloads had the same shape from the other side: the PDF
   *  flag is on the PUBLIC response, so the button was drawn, but its handler
   *  comes from the session, so it was drawn disabled — the exact thing this
   *  change says it will never do.
   *
   *  False means POLICY UNKNOWN: the sections that depend on it render nothing
   *  and appear when the answer does. A section arriving a moment late is
   *  honest; a wrong instruction is not. */
  policyKnown?: boolean;
  canMove?: boolean;
  canCancel?: boolean;
  /** Edit answers, on the ANSWERS section (2026-09-07). It had no home when the
   *  platform's action card was removed, and a card for the two leftovers was
   *  the last thing on the page the admin had not placed. Folded into the
   *  section it belongs to instead of a fourteenth registry entry: the control
   *  that edits the answers lives with the answers. */
  canEditAnswers?: boolean;
  /** May THIS booking's summary PDF be downloaded — the session's own
   *  `actions.summary_pdf.allowed`.
   *
   *  Separate from `CSRenderCtx.actions.summaryPdf`, and the distinction is the
   *  whole point: the public flag says whether the OPERATION EXISTS (true for
   *  every tenant today), the session flag says whether THIS BOOKING may use it.
   *  Taking visibility from the first and the handler from the second drew a
   *  greyed-out button for any booking whose policy withheld the PDF — the same
   *  disabled-button defect this change twice says it will never ship. One flag
   *  owns both now. */
  canPdf?: boolean;
  /** See what changed — the rebook history — on the MANAGE section, beside the
   *  two actions whose history it records. */
  canHistory?: boolean;
  /** The change window has passed. Renders the reason in place of the buttons. */
  windowClosed?: boolean;
  dataExport?: boolean;
  /** Reveal is available: masking is on AND a step-up channel exists. The
   *  settings validator refuses masking with no channels, so these travel
   *  together and a masked-with-no-way-out state cannot be reached. */
  canReveal?: boolean;
  masked?: boolean;
  onMove?: () => void;
  onCancel?: () => void;
  onEditAnswers?: () => void;
  onHistory?: () => void;
  onIcs?: () => void;
  onPdf?: () => void;
  onExport?: () => void;
  onReveal?: () => void;
  /** LOCAL PATCH (platform side, 2026-09-08, JAI-1292): the session's `book_another` target, when the tenant offers one. */
  onBookAnother?: () => void;
  /** Key of the control mid-flight, so it can say so and the rest can lock. */
  busy?: string | null;
}

export interface CSRenderCtx {
  /** True on the CUSTOMER surface (`src/manage`). Default false = the designer. */
  live?: boolean;
  /** Server-owned download flags. Absent = the designer, which shows both. */
  actions?: { summaryPdf: boolean; walletPass: boolean };
  /** What the controls may do. Absent = the designer's inert preview. */
  wiring?: CSWiring;
}

export function CSCanvas(props: {
  state: CSState;
  type: CSType;
  booking: CSBooking;
  fields: CSPageFields;
  sel: string | null;
  onSelect: (id: string | null) => void;
  accent?: string;
  /** Only the Answers section reads it, and only where the type has services
   *  to choose between. */
  answersPick?: CSAnswersPick | null;
  surface?: CSSurface;
  /** What happened to the booking. Defaults to `confirmed`, which is the only
   *  outcome a journey end page can have. */
  pageState?: CSPageState;
  /** True on the CUSTOMER surface (`src/manage`): authoring annotations and
   *  authoring empty states are suppressed, and the download pills follow the
   *  server's flags. Default false = the designer. */
  live?: boolean;
  actions?: { summaryPdf: boolean; walletPass: boolean };
  /** Supplied to get the per-section controls on the canvas — move up, move
   *  down, remove — the same three the block canvas has. Omitted, the canvas is
   *  read-only and no controls render. */
  onChange?: (next: CSState) => void;
  /** What the page's controls may do, and what pressing them runs. Absent = the
   *  designer's inert preview of the same controls.
   *
   *  Declared here as well as on `CSRenderCtx` because they are two different
   *  types: the ctx is what `sectionBody` receives, this is what a CALLER passes.
   *  It was added to the ctx and missed here, and the preview bundles without
   *  typechecking, so it built locally and failed the kit's strict config
   *  (TS2339 in `CSCanvas`, TS2322 at every call site). */
  wiring?: CSWiring;
  /** The page's authored blocks (`content[]` on the document). Rendered after
   *  the sections, read-only; the designer previews them on its own block
   *  canvas and does not pass them here. LOCAL PATCH (platform side, 2026-09-09). */
  content?: ReadonlyArray<{ uid: string; type: string; text?: unknown }>;
}) {
  const { state, type, booking, fields, sel, onSelect } = props;
  const ctx: CSRenderCtx = { live: props.live === true, actions: props.actions, wiring: props.wiring };
  const surface = props.surface ?? "end";
  const accent = props.accent ?? "#14131F";
  const [hover, setHover] = useState<string | null>(null);
  const st = props.pageState ?? "confirmed";
  /* Hidden, unknown and not-applicable rows all stay on the page and none of
     them render. Unknown is ignored rather than rejected — the same rule the
     document's open interior follows.
     An expired link additionally renders only the three sections that carry no
     booking data: the link has just been judged not to identify a booking. */
  const shown = state.filter((r) => !r.hidden && csDef(r.type) && csApplies(r.type, type, surface)
    && (st !== "expired" || EXPIRED_ONLY.indexOf(r.type) > -1));
  /* Nudge moves within the WHOLE document, not within the shown subset: an
     index from a filtered list would jump a section past every hidden one
     between it and its neighbour. */
  const nudge = (uid: string, delta: number) => {
    if (!props.onChange) return;
    const from = state.findIndex((r) => r.uid === uid);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= state.length) return;
    props.onChange(csMove(state, from, to));
  };
  return (
    <div className="nj-sections" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {shown.map((r) => {
        const d = csDef(r.type)!;
        const on = sel === r.uid;
        const i = state.findIndex((x) => x.uid === r.uid);
        /* × on hover so a section can go without being selected first; the
           arrows only when selected, which keeps them off screen while still
           leaving a path to reorder that is not a drag. Same rule as the block
           canvas, so one gesture serves both. */
        const showCtl = !!props.onChange && (on || hover === r.uid) && !d.locked;
        /* LOCAL PATCH (platform side, 2026-09-09): a section with nothing to say
           renders nothing — including its frame. `sectionBody` already answers
           `null` on the live surface for a section whose data is absent (Book
           another with no target, Answers with none, Meeting type on an
           in-person booking, ...); drawing the bordered wrapper around that
           null put an empty box on every customer page for each of them. The
           designer arms return their placeholder text, so nothing changes there. */
        const body = sectionBody(d, type, booking, fields, accent, d.id === "answers" ? (props.answersPick ?? null) : null, st, ctx);
        if (body === null) return null;
        return (
          <div key={r.uid} onClick={(e) => { e.stopPropagation(); onSelect(on ? null : r.uid); }}
            onMouseEnter={() => setHover(r.uid)}
            onMouseLeave={() => setHover((x) => (x === r.uid ? null : x))}
            className="nj-section"
            style={{ position: "relative", padding: "11px 12px", borderRadius: 10, cursor: "pointer", border: `1px solid ${on ? "#5142C7" : "#EDEEF2"}`, outline: on ? "2px solid #5142C7" : "none", outlineOffset: 2, background: "#FFFFFF" }}>
            {on ? <span style={{ position: "absolute", top: -9, left: 9, background: "#5142C7", color: "#FFFFFF", fontSize: 8.5, fontWeight: 800, letterSpacing: "0.05em", borderRadius: 4, padding: "1px 7px" }}>SECTION · {d.label.toUpperCase()}</span> : null}
            {showCtl ? (
              <span onClick={(e) => e.stopPropagation()}
                style={{ position: "absolute", top: -11, right: 4, zIndex: 3, display: "inline-flex", gap: 3, background: "#FFFFFF", padding: 2, borderRadius: 6, boxShadow: "0 1px 4px rgba(20,19,31,0.16)" }}>
                {on ? (<>
                  <button onClick={() => nudge(r.uid, -1)} disabled={i === 0} title="Move up" style={{ ...CTL, opacity: i === 0 ? 0.35 : 1 }}>↑</button>
                  <button onClick={() => nudge(r.uid, 1)} disabled={i === state.length - 1} title="Move down" style={{ ...CTL, opacity: i === state.length - 1 ? 0.35 : 1 }}>↓</button>
                </>) : null}
                <button onClick={() => { if (on) onSelect(null); props.onChange!(csRemove(state, r.uid)); }}
                  title={`Remove ${d.label}`} style={{ ...CTL, borderColor: "#F3D0CB", color: "#C0392B", fontSize: 11 }}>×</button>
              </span>
            ) : null}
            {body}
          </div>
        );
      })}
      {(props.content ?? []).map((blk) => {
        /* LOCAL PATCH (platform side, 2026-09-09): the page's authored blocks
           (`content[]` — heading, text, callout, link, divider) round-trip through
           the store and the public lane but had no render arm on this canvas,
           so a block the operator saved never reached the customer. The
           designer previews them through `BlockCanvas`; this is the read-only
           twin for the live surface, same five kinds, same copy. */
        const text = typeof blk.text === "string" ? blk.text : "";
        if (blk.type === "divider") return <div key={blk.uid} style={{ height: 1, background: "#EDEEF2", margin: "2px 0" }} />;
        if (!text.trim()) return null;
        const card: React.CSSProperties = { padding: "11px 12px", borderRadius: 10, border: "1px solid #EDEEF2", background: "#FFFFFF", fontFamily: FONT };
        if (blk.type === "heading") return <div key={blk.uid} style={{ ...card, fontSize: 15, fontWeight: 800, color: "#14131F" }}>{text}</div>;
        if (blk.type === "callout") return <div key={blk.uid} style={{ ...card, background: "#FFF7E8", borderColor: "#F3D9A4", color: "#5A3E00", fontSize: 13, lineHeight: 1.55 }}>{text}</div>;
        if (blk.type === "link") return <div key={blk.uid} style={{ ...card, fontSize: 13 }}><span style={{ color: accent, fontWeight: 700, textDecoration: "underline" }}>{text}</span></div>;
        return <div key={blk.uid} style={{ ...card, fontSize: 13, color: "#3A3F4B", lineHeight: 1.55 }}>{text}</div>;
      })}
      {shown.length === 0 ? empty(st === "expired"
        ? "An expired link shows only the brand header, the banner and the footer — none of those is on this page."
        : "Every section is switched off — the page would render empty.") : null}
      {st === "expired" && !ctx.live ? (
        /* Authoring annotation, and it was leaking onto the live 404 page — it
           explains the renderer's behaviour to an AUTHOR, and the customer
           reading "sections carrying booking data are withheld" learns only that
           something was hidden from them. Every other note() on this canvas is
           already live-gated; this one was written outside that helper and
           missed it. */
        <div style={{ fontSize: 9.5, color: "#B5BAC6", textAlign: "center", ...MONO }}>
          sections carrying booking data are withheld on an invalid link
        </div>
      ) : null}
      {ctx.live ? null : (
        <div style={{ fontSize: 9.5, color: "#B5BAC6", textAlign: "center", ...MONO }}>
          sample booking · a real one supplies these values
        </div>
      )}
    </div>
  );
}

/* ══ the inspector scope ═══════════════════════════════════════════════ */
export function CSInspector(props: {
  id: string;
  type: CSType;
  fields: CSPageFields;
  onPatch: (patch: CSPageFields) => void;
  hidden: boolean;
  onToggle: () => void;
}) {
  const d = csDef(props.id);
  if (!d) return null;
  const applies = d.types.indexOf(props.type) > -1;
  const f = props.fields;
  return (
    <>
      <hr style={{ border: "none", borderTop: "1px solid #F4F5F7", margin: "4px 0 12px" }} />
      <div style={SEC}>{d.label} — section</div>
      <div style={{ ...NOTE, marginBottom: 10 }}>{d.help}</div>
      {!applies ? (
        <div style={{ border: "1px solid #F3D9A4", background: "#FFFAED", borderRadius: 8, padding: "9px 11px", fontSize: 11, lineHeight: 1.55, color: "#916D0F", marginBottom: 10 }}>
          This section means nothing on a {props.type} journey and will not render.
        </div>
      ) : null}
      {d.locked ? (
        <div style={{ fontSize: 11, color: "#697180", lineHeight: 1.5, marginBottom: 10 }}>🔒 Locked on — it cannot be hidden or removed.</div>
      ) : (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, marginBottom: 10 }}>
          {props.hidden ? "Hidden on this page" : "Shown on this page"}
          <button onClick={props.onToggle} style={{ border: "1px solid #D1D4DC", background: "#FFFFFF", borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>{props.hidden ? "Show" : "Hide"}</button>
        </div>
      )}
      {(d.fields ?? []).indexOf("bannerTitle") > -1 ? (
        <div style={{ marginBottom: 9 }}>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Headline</div>
          <input value={f.bannerTitle ?? ""} onChange={(e) => props.onPatch({ bannerTitle: e.target.value })} placeholder="You're all set" style={{ ...INPUT, fontSize: 12 }} />
          <div style={{ fontSize: 10, color: "#B5BAC6", marginTop: 4, lineHeight: 1.45 }}>Used when the outcome is a plain confirmation. Pending-approval and waitlisted wording is set by the outcome, not here.</div>
        </div>
      ) : null}
      {(d.fields ?? []).indexOf("cancelHours") > -1 ? (
        <div style={{ marginBottom: 9 }}>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Cancellation window (hours)</div>
          <input value={f.cancelHours ?? ""} onChange={(e) => props.onPatch({ cancelHours: e.target.value })} placeholder="24" style={{ ...INPUT, fontSize: 12 }} />
        </div>
      ) : null}
      {(d.fields ?? []).indexOf("note") > -1 ? (
        <div style={{ marginBottom: 9 }}>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Footer note</div>
          <textarea value={f.note ?? ""} onChange={(e) => props.onPatch({ note: e.target.value })} rows={3} style={{ ...INPUT, fontSize: 12, resize: "vertical" }} />
        </div>
      ) : null}
      {!d.fields?.length ? (
        <div style={{ fontSize: 11, color: "#697180", lineHeight: 1.5 }}>
          Nothing to configure — this section renders the booking. {cap(props.type)} journeys can use{" "}
          {csForType(props.type).length} of the {CONFIRMATION_SECTIONS.length} sections.
        </div>
      ) : null}
    </>
  );
}
